import * as React from 'react';
import Table from 'react-bootstrap/Table';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import Pagination from '@mui/material/Pagination';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { NavLink } from 'react-router-dom';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { postRequest } from '../../lib/api';
import { AuthContext } from '../../authContext/context';
import { useContext, useEffect, useState, useRef } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Swal from 'sweetalert2';
import BreadcrumbsWrapper from '../../components/breadcrumbs';
import { checkPermission } from '../../utils/checkPermissions';

export default function Customers() {
  const [customerList, setCustomerList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [searchCustId, setSearchCustId] = useState('');
  const [searchName, setSearchName] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchPhone, setSearchPhone] = useState('');

  const authCtx = useContext(AuthContext);
  const userPermissions = authCtx.permissions;
  const canManageCustomers = checkPermission(userPermissions, 'customers', 'create') ||
                             checkPermission(userPermissions, 'customers', 'edit') ||
                             checkPermission(userPermissions, 'customers', 'delete');
  const canViewDetails = checkPermission(userPermissions, 'customers', 'view_details');
  const canEdit = checkPermission(userPermissions, 'customers', 'edit');
  const canDelete = checkPermission(userPermissions, 'customers', 'delete');
  const debounceRef = useRef();

  const fetchCustomers = async (searchCustIdVal, searchNameVal, searchEmailVal, searchPhoneVal, pageVal = page) => {
    setLoading(true);
    try {
      const payload = {
        customer_id: searchCustIdVal,
        fullname: searchNameVal,
        email: searchEmailVal,
        phone_number: searchPhoneVal,
        page: pageVal,
        limit: pageSize,
      };

      const response = await postRequest('private/customerList', payload, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setCustomerList(response.data.docs || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalDocs(response.data.totalDocs || 0);
      } else {
        setCustomerList([]);
        setTotalPages(1);
        setTotalDocs(0);
      }
    } catch (err) {
      setCustomerList([]);
      setTotalPages(1);
      setTotalDocs(0);
    }
    setLoading(false);
  };

  // Fetch on mount and when page or page size changes
  useEffect(() => {
    fetchCustomers(searchCustId, searchName, searchEmail, searchPhone, page);
    // eslint-disable-next-line
  }, [page, pageSize, authCtx.token]);

  // Change how many records show per page (resets to first page)
  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  // Debounced search on custId, name, email or phone change
  useEffect(() => {
    setPage(1); // Reset to first page on search
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCustomers(searchCustId, searchName, searchEmail, searchPhone, 1);
    }, 400);
    // eslint-disable-next-line
  }, [searchCustId, searchName, searchEmail, searchPhone]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleDelete = async (customerId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this customer!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#bb0000',
      cancelButtonColor: '#aaa',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        const response = await postRequest(`private/deleteCustomer/${customerId}`, {}, authCtx.token);
        if (response?.status === 200) {
          Swal.fire('Deleted!', 'Customer has been deleted.', 'success');

          const payload = {
            customer_id: searchCustId,
            fullname: searchName,
            email: searchEmail,
            phone_number: searchPhone,
            page,
            limit: 10,
          };
          const listResponse = await postRequest('private/customerList', payload, authCtx.token);
          const docs = listResponse?.data?.docs || [];
          const totalPagesAfterDelete = listResponse?.data?.totalPages || 1;

          if (docs.length === 0 && page > 1) {
            setPage((prev) => prev - 1);
          } else {
            setCustomerList(docs);
            setTotalPages(totalPagesAfterDelete);
          }
        } else {
          Swal.fire('Failed!', response?.message || 'Something went wrong.', 'error');
        }
      } catch (err) {
        Swal.fire('Error!', err?.message || 'Something went wrong.', 'error');
      }
    }
  };

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Customers', path: '' },
        ]}
      />
      <br></br>
      <div className='page-header'>
        <h1 className='mt-0'>Customers</h1>
        {canManageCustomers && (
          <div className='header-right-btn'>
            <Stack spacing={2} direction="row">
              <Button variant="outlined" className='red-outline-btn text-capitalize'>
                <NavLink to="/customers/create" className="red-link-text">
                  <PersonAddOutlinedIcon /> &nbsp; Add New Customer
                </NavLink>
              </Button>
            </Stack>
          </div>
        )}
      </div>
      <div className='page-top-formview mb-3'>
        <Form>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="searchCustId">
              <Form.Label>Customer ID</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter customer ID"
                value={searchCustId}
                onChange={e => {
                  setSearchCustId(e.target.value);
                  setPage(1);
                }}
              />
            </Form.Group>
            <Form.Group as={Col} controlId="searchName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                value={searchName}
                onChange={e => {
                  setSearchName(e.target.value);
                  setPage(1);
                }}
              />
            </Form.Group>
            <Form.Group as={Col} controlId="searchEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter email"
                value={searchEmail}
                onChange={e => {
                  setSearchEmail(e.target.value);
                  setPage(1);
                }}
              />
            </Form.Group>
            <Form.Group as={Col} controlId="searchPhone">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter phone"
                value={searchPhone}
                onChange={e => {
                  setSearchPhone(e.target.value);
                  setPage(1);
                }}
              />
            </Form.Group>
          </Row>
        </Form>
      </div>

      <div>
        <Table striped responsive bordered>
          <thead>
            <tr>
              <th>Customer ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Partner</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center' }}>
                  <CircularProgress size={28} />
                </td>
              </tr>
            ) : customerList.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center' }}>
                  No customers found.
                </td>
              </tr>
            ) : (
              customerList.map((customer) => (
                <tr key={customer._id}>
                  <td>{customer?.customer_id || '-'}</td>
                  <th>{customer?.forename} {customer?.surname}</th>
                  <td>{customer?.email || '-'}</td>
                  <td>{customer?.phone_number || '-'}</td>
                  <td>{customer?.partner || '-'}</td>
                  <td>
                    {canViewDetails && (
                      <span
                        className='red-link-text cursor-pointer w-18'
                        title="View"
                        style={{ marginRight: 8 }}
                      >
                        <NavLink to={`/customers/view/${customer?.customer_id}`} className="red-link-text">
                          <VisibilityOutlinedIcon />
                        </NavLink>
                      </span>
                    )}
                    {canEdit && (
                      <span
                        className='red-link-text cursor-pointer w-18'
                        title="Edit"
                        style={{ marginRight: 8 }}
                      >
                        <NavLink to={`/customer/edit/${customer.customer_id}`} className="red-link-text">
                          <EditOutlinedIcon />
                        </NavLink>
                      </span>
                    )}
                    {canDelete && (
                      <span
                        className='red-link-text cursor-pointer w-18'
                        title="Delete"
                        onClick={() => handleDelete(customer.customer_id)}
                      >
                        <DeleteOutlineOutlinedIcon />
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        {/* Pagination */}
        {customerList.length > 0 && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
              <span className="text-muted small">
                Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, totalDocs)} of {totalDocs} customers
              </span>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="customer-page-size-label">Rows per page</InputLabel>
                <Select
                  labelId="customer-page-size-label"
                  label="Rows per page"
                  value={pageSize}
                  onChange={handlePageSizeChange}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                size="medium"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  '& .Mui-selected': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important',
                    color: '#fff !important',
                  },
                  '& .MuiPaginationItem-root': {
                    color: 'var(--bs-red-color)',
                    borderColor: 'var(--bs-red-color)',
                  },
                }}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
